// src/pages/Employment/ViewEmployment.jsx
import React from 'react';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaClock, 
  FaMoneyBillWave,
  FaBriefcase,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaCheckCircle,
  FaStar,
  FaCalendarAlt,
  FaInfoCircle,
  FaTag,
  FaEye,
  FaUsers,
  FaTimes,
  FaLink,
  FaWhatsapp,
  FaIdCard,
  FaCalendarCheck,
} from 'react-icons/fa';

function ViewEmployment({ data }) {
  if (!data) {
    return <div className="text-center py-4">No data available</div>;
  }

  console.log("ViewEmployment Data:", data); // Debug log

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning text-dark', label: 'Pending' },
      'approved': { class: 'badge bg-success', label: 'Approved' },
      'rejected': { class: 'badge bg-danger', label: 'Rejected' },
      'expired': { class: 'badge bg-secondary', label: 'Expired' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      'job': 'Job',
      'business': 'Business',
      'internship': 'Internship',
      'freelance': 'Freelance',
      'consultancy': 'Consultancy',
    };
    return categoryMap[category] || category || 'N/A';
  };

  const getTypeLabel = (type) => {
    const typeMap = {
      'full_time': 'Full Time',
      'part_time': 'Part Time',
      'contract': 'Contract',
      'remote': 'Remote',
      'hybrid': 'Hybrid',
    };
    return typeMap[type] || type || 'N/A';
  };

  // Get value with proper handling
  const getValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  };

  // Check if value is a valid URL
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const displayFields = [
    // Basic Information
    { key: 'title', label: 'Job Title', icon: <FaBriefcase /> },
    { key: 'company_name', label: 'Company Name', icon: <FaBuilding /> },
    { key: 'category', label: 'Category', icon: <FaTag />, format: getCategoryLabel },
    { key: 'type', label: 'Type', icon: <FaClock />, format: getTypeLabel },
    
    // Location & Salary
    { key: 'location', label: 'Location', icon: <FaMapMarkerAlt /> },
    { key: 'salary_range', label: 'Salary Range', icon: <FaMoneyBillWave /> },
    { key: 'experience_required', label: 'Experience Required', icon: <FaUser /> },
    { key: 'qualification', label: 'Qualification', icon: <FaInfoCircle /> },
    
    // Description
    { key: 'description', label: 'Description', icon: <FaInfoCircle /> },
    { key: 'requirements', label: 'Requirements', icon: <FaCheckCircle /> },
    { key: 'responsibilities', label: 'Responsibilities', icon: <FaCheckCircle /> },
    
    // Address Details
    { key: 'address', label: 'Address', icon: <FaMapMarkerAlt /> },
    { key: 'city', label: 'City', icon: <FaMapMarkerAlt /> },
    { key: 'state', label: 'State', icon: <FaMapMarkerAlt /> },
    { key: 'country', label: 'Country', icon: <FaGlobe /> },
    { key: 'pincode', label: 'Pincode', icon: <FaMapMarkerAlt /> },
    
    // Contact Information
    { key: 'email', label: 'Email', icon: <FaEnvelope /> },
    { key: 'phone', label: 'Phone', icon: <FaPhone /> },
    { key: 'whatsapp', label: 'WhatsApp', icon: <FaWhatsapp /> },
    { key: 'contact_person', label: 'Contact Person', icon: <FaUser /> },
    { key: 'contact_phone', label: 'Contact Phone', icon: <FaPhone /> },
    
    // Company Details
    { key: 'company_website', label: 'Company Website', icon: <FaLink /> },
    { key: 'company_email', label: 'Company Email', icon: <FaEnvelope /> },
    
    // Dates
    { key: 'application_deadline', label: 'Application Deadline', icon: <FaCalendarCheck />, format: formatDate },
    
    // Status & Meta
    { key: 'status', label: 'Status', icon: <FaCheckCircle />, format: getStatusBadge },
    { key: 'is_featured', label: 'Featured', icon: <FaStar />, format: (value) => value ? 'Yes' : 'No' },
    { key: 'is_verified', label: 'Verified', icon: <FaCheckCircle />, format: (value) => value ? 'Yes' : 'No' },
    { key: 'is_active', label: 'Active', icon: <FaCheckCircle />, format: (value) => value ? 'Yes' : 'No' },
    
    // Statistics
    { key: 'view_count', label: 'Views', icon: <FaEye /> },
    { key: 'application_count', label: 'Applications', icon: <FaUsers /> },
    
    // Meta Information
    { key: 'meta_title', label: 'Meta Title', icon: <FaInfoCircle /> },
    { key: 'meta_description', label: 'Meta Description', icon: <FaInfoCircle /> },
    { key: 'meta_keywords', label: 'Meta Keywords', icon: <FaTag /> },
    
    // Timestamps
    { key: 'created_at', label: 'Created At', icon: <FaClock />, format: formatDateTime },
    { key: 'updated_at', label: 'Updated At', icon: <FaClock />, format: formatDateTime },
  ];

  // Check if data has image
  const hasImage = data.company_logo || data.image_path;

  return (
    <div className="view-employment-container">
      {/* Header with Title */}
      <div className="text-center mb-4">
        <h4 className="fw-bold text-primary">{data.title || 'Job Details'}</h4>
        <p className="text-muted">{data.company_name || 'Company'}</p>
      </div>

      {/* Company Logo */}
      {hasImage && (
        <div className="text-center mb-3">
          <img
            src={data.company_logo || data.image_path}
            alt={data.company_name || 'Company Logo'}
            style={{ 
              maxWidth: '150px', 
              maxHeight: '150px', 
              objectFit: 'contain',
              border: '1px solid #e8eaed',
              borderRadius: '8px',
              padding: '8px'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Featured & Verified Badges */}
      <div className="text-center mb-3">
        {data.is_featured && (
          <span className="badge bg-warning text-dark me-2 p-2">
            <FaStar className="me-1" /> Featured
          </span>
        )}
        {data.is_verified && (
          <span className="badge bg-success me-2 p-2">
            <FaCheckCircle className="me-1" /> Verified
          </span>
        )}
        {data.is_active && (
          <span className="badge bg-info text-dark p-2">
            <FaCheckCircle className="me-1" /> Active
          </span>
        )}
      </div>

      {/* Main Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover">
          <tbody>
            {displayFields.map((field) => {
              // Skip if value is null/undefined and field is not a checkbox type
              if (data[field.key] === null || data[field.key] === undefined) {
                // Still show N/A for all fields except maybe we want to show them
                // We'll let getValue handle it
              }
              
              let value = data[field.key];
              
              // Format the value if formatter exists
              if (field.format) {
                value = field.format(value);
              } else {
                value = getValue(value);
              }

              // Special handling for website links
              if (field.key === 'company_website' && value !== 'N/A' && isValidUrl(value)) {
                value = <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
              }

              // Special handling for email
              if (field.key === 'email' && value !== 'N/A' && value.includes('@')) {
                value = <a href={`mailto:${value}`}>{value}</a>;
              }

              return (
                <tr key={field.key}>
                  <th style={{ width: '30%', backgroundColor: '#f8f9fa', verticalAlign: 'top' }}>
                    {field.icon && <span className="me-2">{field.icon}</span>}
                    {field.label}
                  </th>
                  <td style={{ verticalAlign: 'top' }}>
                    {field.key === 'description' || field.key === 'requirements' || field.key === 'responsibilities' ? (
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                        {value}
                      </div>
                    ) : (
                      value
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quick Actions */}
      <div className="mt-4 d-flex gap-2 flex-wrap justify-content-center border-top pt-3">
        {data.status === 'pending' && (
          <>
            <button 
              className="btn btn-success"
              onClick={() => {
                if (window.confirm('Are you sure you want to approve this job?')) {
                  // Call API to approve
                }
              }}
            >
              <FaCheckCircle className="me-1" /> Approve
            </button>
            <button 
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm('Are you sure you want to reject this job?')) {
                  // Call API to reject
                }
              }}
            >
              <FaTimes className="me-1" /> Reject
            </button>
          </>
        )}
        {data.status === 'approved' && (
          <button 
            className="btn btn-warning"
            onClick={() => {
              if (window.confirm('Are you sure you want to mark this as expired?')) {
                // Call API to expire
              }
            }}
          >
            <FaClock className="me-1" /> Mark as Expired
          </button>
        )}
      </div>
    </div>
  );
}

export default ViewEmployment;